/**
 * Utility for listening to window messages and handling specific message types
 */

export interface WindowMessage {
  type: string;
  data?: unknown;
  widget_id?: string; // Widget ID the message is intended for (bot identity)
  placement_id?: string; // Placement/placement key the message is intended for
}

export interface MessageListenerOptions {
  onOpenChat?: () => void;
  onCloseChat?: () => void;
  targetOrigin?: string; // Optional origin validation for security
  widgetId?: string; // Widget ID to filter messages for this specific widget instance
  placementKey?: string; // Effective placement key (placement-id || widget-id) to target one placement
}

/**
 * Sets up a window message listener that responds to OPEN_LB_CHAT and CLOSE_LB_CHAT messages
 * @param options Configuration options for the message listener
 * @returns Cleanup function to remove the event listener
 */
export const setupWindowMessageListener = (options: MessageListenerOptions): (() => void) => {
  const { onOpenChat, onCloseChat, targetOrigin, widgetId, placementKey } = options;

  // Handler function for window messages
  const handleMessage = (event: MessageEvent): void => {
    // Optional origin validation for security
    if (targetOrigin && event.origin !== targetOrigin) {
      return;
    }

    // Handle the message based on its type
    if (event.data && typeof event.data === "object") {
      const message: WindowMessage = event.data;

      // Filter messages by widget_id if specified.
      // Only process messages that either have no widget_id (for backward compatibility)
      // or have a widget_id that matches the current widget instance (bot identity).
      if (widgetId && message.widget_id && message.widget_id !== widgetId) {
        return; // Ignore messages for other widgets
      }

      // Further filter by placement_id, but only when the message also carries a widget_id.
      // A placement is a sub-scope of a widget, and placement keys are author-chosen and
      // not globally unique across bots — so placement_id alone can't safely address a
      // placement. Requiring both fields keeps the documented contract: a message missing
      // either targeting field stays a broadcast. With both present (and widget_id already
      // matched above), this targets exactly one placement of the matched widget.
      if (
        message.widget_id &&
        placementKey &&
        message.placement_id &&
        message.placement_id !== placementKey
      ) {
        return; // Ignore messages for other placements
      }

      // Handle OPEN_LB_CHAT message type
      if (message.type === "OPEN_LB_CHAT" && onOpenChat) {
        onOpenChat();
      }

      // Handle CLOSE_LB_CHAT message type
      if (message.type === "CLOSE_LB_CHAT" && onCloseChat) {
        onCloseChat();
      }
    }
  };

  // Add the event listener
  window.addEventListener("message", handleMessage);

  // Return cleanup function
  return () => {
    window.removeEventListener("message", handleMessage);
  };
};
